'use client'

import { useEffect, useRef } from 'react'
import { Mic } from 'lucide-react'
import type { PlaybackClock } from '@/hooks/use-santa-voice-player'
import type { ChatEntry } from '@/lib/santa-chat/chat-state'
import { VoiceNoteBubble, type VoiceNoteBubbleLabels } from './voice-note-bubble'

export interface ChatHistoryLabels extends VoiceNoteBubbleLabels {
  santaRecording: string
}

interface ChatHistoryProps {
  entries: ChatEntry[]
  playingId: string | null
  loadingId: string | null
  clock: PlaybackClock
  /** Papá Noel está contestando: burbuja de «grabando» al final del historial. */
  santaRecording: boolean
  labels: ChatHistoryLabels
  onPlay: (entry: ChatEntry) => void
  onResend: (localId: string) => void
  resendDisabled: boolean
}

const DOT_DELAYS = ['0ms', '150ms', '300ms']

/**
 * Lo que una mensajería enseña mientras el otro graba, donde el niño está
 * mirando (la cabecera lo dice en texto, que no todos saben leer).
 */
function SantaRecordingBubble({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      className="flex items-center gap-2 self-start rounded-2xl rounded-tl-sm bg-card px-4 py-3 text-accent shadow"
    >
      <Mic aria-hidden className="size-5 motion-safe:animate-pulse" />
      {DOT_DELAYS.map((delay) => (
        <span
          key={delay}
          aria-hidden
          style={{ animationDelay: delay }}
          className="size-2.5 rounded-full bg-current motion-safe:animate-bounce"
        />
      ))}
    </div>
  )
}

/** Historial desplazable; baja solo al último mensaje cuando llega uno nuevo. */
export function ChatHistory(props: ChatHistoryProps) {
  const { entries, playingId, loadingId, clock, santaRecording, labels, onPlay, onResend, resendDisabled } = props
  const endRef = useRef<HTMLDivElement>(null)
  const lastId = entries.at(-1)?.id

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' })
  }, [lastId, santaRecording])

  return (
    <div role="log" aria-live="polite" className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
      {entries.map((entry) => (
        <VoiceNoteBubble
          key={entry.id}
          entry={entry}
          labels={labels}
          playing={entry.id === playingId}
          loading={entry.id === loadingId}
          clock={clock}
          onPlay={onPlay}
          onResend={onResend}
          resendDisabled={resendDisabled}
        />
      ))}
      {santaRecording && <SantaRecordingBubble label={labels.santaRecording} />}
      <div ref={endRef} />
    </div>
  )
}
